import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { getDefaultOgImageUrl, getPublicSiteUrl } from '../../pages/new/siteUrl';
import { JsonLdScript } from './JsonLdScript';
import { buildOrganizationJsonLd, buildWebSiteJsonLd } from './jsonLd';
import { usePrerenderReady } from './usePrerenderReady';

const DEFAULT_TITLE = 'Charlee.ai';
const DEFAULT_DESCRIPTION = 'Charlee.ai — AI for insurance claims and underwriting.';

function trimNonEmpty(value) {
  if (value == null || typeof value !== 'string') return '';
  const t = value.trim();
  return t || '';
}

function normalizePath(path) {
  const p = trimNonEmpty(path);
  if (!p) return '';
  return p.startsWith('/') ? p : `/${p}`;
}

/**
 * SEO helmet for static marketing/product pages.
 * Define pageTitle, metaDescription, canonicalPath (and optional fields) in each page file.
 */
export function PageSeoHelmet({
  pageTitle,
  metaDescription,
  canonicalPath,
  canonicalUrl: canonicalUrlProp,
  ogImage,
  ogType = 'website',
  noIndex = false,
  siteJsonLd = false,
}) {
  const title = trimNonEmpty(pageTitle) || DEFAULT_TITLE;
  const description = trimNonEmpty(metaDescription) || DEFAULT_DESCRIPTION;
  const image = trimNonEmpty(ogImage) || getDefaultOgImageUrl();
  const ogTypeSafe = ogType === 'article' ? 'article' : 'website';

  const canonicalUrl = useMemo(() => {
    const explicit = trimNonEmpty(canonicalUrlProp);
    if (explicit) return explicit;
    const siteUrl = getPublicSiteUrl();
    const path = normalizePath(canonicalPath);
    if (!siteUrl || !path) return '';
    return `${siteUrl}${path}`;
  }, [canonicalUrlProp, canonicalPath]);

  const siteUrl = getPublicSiteUrl();
  const jsonLdData = useMemo(() => {
    if (!siteJsonLd || !siteUrl) return [];
    const logoUrl = getDefaultOgImageUrl();
    return [
      buildOrganizationJsonLd({ siteUrl, logoUrl }),
      buildWebSiteJsonLd({ siteUrl }),
    ];
  }, [siteJsonLd, siteUrl]);

  usePrerenderReady(true);

  return (
    <>
    {siteJsonLd ? <JsonLdScript data={jsonLdData} /> : null}
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
      <meta property="og:type" content={ogTypeSafe} />
      {image ? <meta property="og:image" content={image} /> : null}
      <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {image ? <meta name="twitter:image" content={image} /> : null}
      {noIndex ? <meta name="robots" content="noindex" /> : null}
    </Helmet>
    </>
  );
}
